<template>
  <CCard v-if="!file.isPlaceholder" v-tooltip="tooltip" class="c-media-file-card" :class="classes">
    <div class="c-media-file-card-content">
      <CMediaFile2 limitedControls :file :files :context/>

      <CShelf class="c-media-file-card-statuses">
        <CIcon v-if="file.isWarranty" name="shield-flag" class="c-media-file-card-icon"/>
        <CIcon v-if="file.isLocked" name="eye-off-outline" class="c-media-file-card-icon"/>
        <CIcon v-if="file.note" name="comment-chat-bubble" class="c-media-file-card-icon"/>
      </CShelf>

      <CShelf v-if="file.type === MediaFileType.PDF" class="c-media-file-card-description">
        <CIcon name="document-pdf"/>
        <CEllipsis>{{ file.description }}</CEllipsis>
      </CShelf>

      <CIcon v-if="file.type === MediaFileType.AUDIO" name="audio-microphone" :size="32" class="c-media-file-audio-icon"/>
    </div>
  </CCard>

  <CCard v-else class="c-media-file-card">
    <div class="c-media-file-card-content">
      <div class="c-media-file-placeholder">
        <CLoader :size="40"/>
      </div>
    </div>
  </CCard>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import MediaContext2 from '@/general/models/MediaContext2';
  import MediaFile2 from '@/general/models/MediaFile2';
  import ReferenceService from '@/general/services/ReferenceService';
  import CCard from '@/shared/components/CCard.vue';
  import CEllipsis from '@/shared/components/CEllipsis.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import CShelf from '@/shared/components/CShelf.vue';
  import MediaFileType from '@/shared/constants/MediaFileType';
  import { formatDate } from '@/shared/utils/dateTime';
  import CMediaFile2 from './CMediaFile2.vue';
  import CLoader from '@/shared/components/CLoader.vue';

  const props = defineProps<{
    context?: MediaContext2;
    file: MediaFile2
    files?: Array<MediaFile2>;
  }>();

  const classes = computed(() => {
    switch (props.file.type) {
      case MediaFileType.AUDIO:
        return 'c-media-file-card-audio';
      case MediaFileType.PDF:
        return 'c-media-file-card-pdf';
      case MediaFileType.VIDEO:
        return 'c-media-file-card-video';
      case MediaFileType.IMAGE:
        return 'c-media-file-card-image';
    }
  });

  const tooltip = computed(() => {
    return [formatDate(props.file.createdOn), ReferenceService.getUser(props.file.createdBy?.id)?.fullName, props.file.note].filter(Boolean).join(' - ');
  });
</script>

<style lang="scss">
  .c-media-file-card {
    aspect-ratio: 1 / 1;
    height: 9rem;
    flex-shrink: 0;
    padding: 0;
    border-radius: 10px;

    &-icon {
      color: $primary;
    }

    &-content {
      background: $neutral-200;
      overflow: hidden;
      position: relative;
      height: 100%;
      width: 100%;

      .c-media-file {
        padding: 0;
        width: 100%;
        height: 100%;

        &-thumbnail {
          object-fit: cover;
          width: 100%;
          height: 100%;
        }

        &-audio {
          position: absolute;
          bottom: 0;
          height: auto;
          padding: .25rem;
          background-color: $neutral-300;
        }

        &-video .js-player {
          aspect-ratio: 1;
          object-fit: cover;
        }

        &-pdf {
          width: 100%;
          height: 100%;
        }

        &-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
      }
    }

    &-audio {
      .c-media-file-card-content {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }

    &-statuses {
      position: absolute;
      top: 0;
      left: 0;
      padding: 0.5rem;
    }

    &-description {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 33.3%;
      padding: 0.5rem;
      width: 100%;
      color: $neutral-0;
      font-size: 0.8rem;
      background: linear-gradient(180deg, transparent 0%, rgba($neutral-900, 1) 100%);
    }
  }

</style>
